// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { constants } from "@flip/XDL/constants";
import { color, hideInputNumberArrows, maxLengthAttr, mergeWithCss, xMask, xScreen } from "@flip/XDL/Utilities";
import isNumeric from "@insite/client-framework/Common/isNumeric";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps, ButtonProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { FormFieldStyle } from "@insite/mobius/FormField";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import { Clickable, Typography, VisuallyHidden } from "@insite/mobius/index";
import TextField, { TextFieldPresentationProps, TextFieldProps } from "@insite/mobius/TextField";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

type ButtonType = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "disabled">;
interface OwnProps extends Omit<TextFieldProps, "css"> {
    decreaseButtonProps?: ButtonType;
    increaseButtonProps?: ButtonType;
    showActionButton?: boolean;
    editable?: boolean;
    extendedStyles?: XngCartLineQuantityStyles;
    label?: string;
}

type Props = OwnProps;

export interface XngCartLineQuantityStyles {
    editableQuantityTextField?: TextFieldPresentationProps;
    wrapper?: InjectableCss;
    countButton?: ClickablePresentationProps;
    increaseButton?: ClickablePresentationProps;
    decreaseButton?: ClickablePresentationProps;
    countIcon?: IconPresentationProps;
    disabledTextField?: TextFieldPresentationProps;
}

export const xngCartLineQuantityStyles: XngCartLineQuantityStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            position: relative;
            background: ${color("common.white")};
            border: 1px solid ${color("common.ui5")};
            height: 30px;
            max-width: 106px;
            min-width: 106px;
            border-radius: 4px;

            &:hover {
                border-color: ${color("common.ui13")};
            }

            &:focus-within {
                border-color: ${color("common.ui12")};
            }

            ${({ showActionButton }: any) =>
                showActionButton &&
                css`
                    & > ${FormFieldStyle} {
                        padding-left: 27px;
                        padding-right: 27px;
                    }
                `}

            ${({ disabled }: any) =>
                disabled &&
                css`
                    color: ${color("text.main")};
                    background: ${color("common.disabled", 0.1)};
                `}
        `,
    },
    editableQuantityTextField: {
        mergeCss: true,
        backgroundColor: "transparent",
        sizeVariant: "small",
        labelProps: {
            variant: "legend",
            css: css`
                ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                    display: none;
                }
            `,
        },
        cssOverrides: {
            formField: css`
                && input:not(.react-datetime-picker__inputGroup__input):not([name="datetime"]) {
                    &,
                    &:active,
                    &:hover,
                    &:focus {
                        outline: none;
                        border: 0;
                        box-shadow: none;
                        &:not(:disabled) {
                            border: 0;
                            box-shadow: none;
                        }
                    }
                }
            `,
            formInputWrapper: css`
                &&&& {
                    margin-top: 0;
                }
            `,

            inputSelect: css`
                height: 26px;
                font-size: 14px;
                letter-spacing: 0;
                line-height: 16px;
                text-align: center;
                padding: 0 !important;
                border: none;
                box-shadow: none !important;
                &:hover,
                &:focus {
                    border: none;
                    box-shadow: none !important;
                }
                &:disabled {
                    background-color: ${color("common.background")};
                }

                ${hideInputNumberArrows}
            `,
        },
    },
    countButton: {
        css: css`
            position: absolute;
            height: 26px;
            padding: 0 8px;
            display: flex;
            align-items: center;
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            ${IconWrapper} {
                color: ${color("common.ui3")};
            }
            &:hover:not(:disabled) {
                ${IconWrapper} {
                    color: ${color("primary")};
                }
            }
            &:focus {
                outline: none;
            }
        `,
    },
    countIcon: {
        color: "common.ui5",
        size: 14,
        mergeCss: true,
    },
    disabledTextField: {
        cssOverrides: {
            formField: css`
                max-width: 92px;
            `,
            inputSelect: css`
                max-width: 92px;
                width: 100%;
                height: 26px;
                text-align: center;
                border: 1px solid ${color("common.ui6")};
                background: ${color("common.ui14")};
                &:disabled {
                    color: ${color("text.main")};
                    background: ${color("common.ui4")};
                }
                &:hover {
                    border: 1px solid ${color("common.ui6")};
                }
            `,
        },
    },
};

xngCartLineQuantityStyles.decreaseButton = mergeWithCss(xngCartLineQuantityStyles.countButton, {
    css: css`
        left: 0;
    `,
});

xngCartLineQuantityStyles.increaseButton = mergeWithCss(xngCartLineQuantityStyles.countButton, {
    css: css`
        right: 0;
    `,
});

const enterKey = 13;

const XngCartLineQuantity: FC<Props> = ({
    id,
    extendedStyles,
    decreaseButtonProps,
    increaseButtonProps,
    disabled: disabledProps,
    value: currentValue,
    onChange: onChangeTextField,
    onBlur: onBlurTextField,
    onClick: onClickTextField,
    onKeyDown: onKeyDownTextField,
    showActionButton = true,
    editable = true,
    label,
    ...qtyFieldsProps
}) => {
    const [styles] = useState(() => mergeWithCss(xngCartLineQuantityStyles, extendedStyles));
    const [callOnSubmitSafety, setCallOnSubmitSafety] = useState(false);

    const disabled = !callOnSubmitSafety && disabledProps;

    /**
     * UseEffect to wait until data change in cartQuantity state
     * then we do submit
     * src/FrontEnd/modules/client-framework/src/Common/Hooks/useAccessibleSubmit.ts
     */
    useEffect(() => {
        if (callOnSubmitSafety) {
            onBlurHandler();
            setCallOnSubmitSafety(false);
        }
    }, [currentValue]);

    const onClickHandler = (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        event.currentTarget.focus();
        onClickTextField?.(event);
    };

    const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.currentTarget.value = !event.currentTarget.value ? "" : xMask.qty(event.currentTarget.value);
        if (event.currentTarget.value.length > maxLengthAttr.qty) {
            event.currentTarget.value = `${currentValue}`;
        }

        onChangeTextField?.(event);
    };

    const onKeyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === enterKey && !onKeyDownTextField) {
            event.stopPropagation();
            onBlurTextField?.(event as any);
        }
    };

    const onBlurHandler = (event?: React.ChangeEvent<HTMLInputElement>) => {
        /**
         * We skip this validation when a user click on increase/decrease button
         * In this case, event.currentTarget will be equal null
         */
        if (event?.currentTarget) {
            event.currentTarget.value = Number(xMask.qty(event.currentTarget.value || "0")) as any;
            onChangeHandler(event);
        }
        onBlurTextField?.(event as any);
    };

    const onDecreaseClickHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && isNumeric(`${currentValue}`) && Number(currentValue) - 1 >= 0
                ? Number(currentValue) - 1
                : undefined;

        if (!decreaseButtonProps?.onClick) {
            if (!nextValue) {
                return;
            }

            event.currentTarget.value = xMask.qty(`${nextValue}`);
            onChangeHandler(event as any);
            setCallOnSubmitSafety(true);
            return;
        }

        decreaseButtonProps?.onClick?.(event);
    };

    const onIncreaseClickHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (disabled) {
            return;
        }

        const nextValue =
            currentValue && isNumeric(`${currentValue}`) && `${Number(currentValue) + 1}`.length <= maxLengthAttr.qty
                ? Number(currentValue) + 1
                : undefined;

        if (!increaseButtonProps?.onClick) {
            if (!nextValue) {
                return;
            }

            event.currentTarget.value = xMask.qty(`${nextValue}`);
            onChangeHandler(event as any);
            setCallOnSubmitSafety(true);
            return;
        }

        increaseButtonProps?.onClick?.(event);
    };

    const textFieldId = `orderline-qty-input-${id}-${uniqueId()}`;

    return (
        <StyledWrapper {...styles.wrapper} {...{ showActionButton, disabled }} data-test-selectore="quantity-field">
            {showActionButton && (
                <Clickable
                    {...decreaseButtonProps}
                    {...styles.decreaseButton}
                    onClick={onDecreaseClickHandler}
                    disabled={disabled || Number(currentValue) <= 1 || !editable}
                    aria-label="decrease quantity"
                >
                    <Icon {...styles.countIcon} src={Minus} />
                </Clickable>
            )}
            <VisuallyHidden as="label" htmlFor={textFieldId}>
                {translate("QTY")}
            </VisuallyHidden>
            <TextField
                type="qty"
                maxLength={maxLengthAttr.qty}
                id={textFieldId}
                disabled={disabled || !editable}
                value={currentValue}
                onChange={onChangeHandler}
                onBlur={onBlurHandler}
                onClick={onClickHandler}
                onKeyDown={onKeyDownHandler}
                {...styles.editableQuantityTextField}
                {...qtyFieldsProps}
            />
            {showActionButton && (
                <Clickable
                    {...increaseButtonProps}
                    {...styles.increaseButton}
                    disabled={disabled || Number(currentValue) >= 999999 || !editable}
                    onClick={onIncreaseClickHandler}
                    aria-label="increase quantity"
                >
                    <Icon {...styles.countIcon} src={Plus} />
                </Clickable>
            )}
        </StyledWrapper>
    );
};

export default XngCartLineQuantity;
